/**
 * Kullanıcı rolleri hiyerarşisi:
 * SUPER_ADMIN > TENANT_ADMIN > PM > TL > EMP
 *                            > MGMT       (yatay — salt-okunur raporlar)
 *                            > CR         (yatay — teklif modülü)
 *                            > ACCOUNTANT (yatay — finans/muhasebe modülü)
 */
export enum UserRole {
  SUPER_ADMIN = 'SUPER_ADMIN',
  TENANT_ADMIN = 'TENANT_ADMIN',
  PM = 'PM',
  TL = 'TL',
  EMP = 'EMP',
  MGMT = 'MGMT',
  CR = 'CR',
  ACCOUNTANT = 'ACCOUNTANT',
}

export enum TenantPlan {
  TRIAL = 'TRIAL',
  STARTER = 'STARTER',
  GROWTH = 'GROWTH',
  BUSINESS = 'BUSINESS',
  ENTERPRISE = 'ENTERPRISE',
}

export enum TenantStatus {
  ACTIVE = 'ACTIVE',
  SUSPENDED = 'SUSPENDED',
  TRIAL = 'TRIAL',
  CANCELLED = 'CANCELLED',
}

export enum UserStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',      // Deaktive edilmiş çalışan — [IK-07]
  PENDING = 'PENDING',        // Aktivasyon e-postası bekleniyor
  LOCKED = 'LOCKED',          // Başarısız giriş denemesi sonrası
}

/**
 * Kullanıcı hesap türü:
 * MEMBER    — sisteme giriş yapıp veri giren kullanıcı (e-posta ile davet).
 * PERSONNEL — girişsiz kayıt personeli (saha çalışanı); yalnızca maaş/bordro/
 *             puantaj verileri tutulur. GlobalUser yok, şifre yok, davet maili yok.
 */
export enum UserAccountType {
  MEMBER = 'MEMBER',
  PERSONNEL = 'PERSONNEL',
}

/**
 * Teklif durum makinesi:
 * DRAFT → PENDING_APPROVAL → SENT → WON | LOST | CANCELLED
 */
export enum ProposalStatus {
  DRAFT = 'DRAFT',
  PENDING_APPROVAL = 'PENDING_APPROVAL',
  SENT = 'SENT',
  WON = 'WON',
  LOST = 'LOST',
  CANCELLED = 'CANCELLED',
}

/**
 * Proje durum makinesi:
 * DRAFT → ACTIVE → ON_HOLD → COMPLETED | CANCELLED
 * [IK-09] Taslak: yalnızca PM+ADMIN görebilir
 * [IK-09] Aktif: atanan tüm üyeler görür, timesheet açılır
 */
export enum ProjectStatus {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  ON_HOLD = 'ON_HOLD',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}

export enum ProjectType {
  FIXED_PRICE = 'FIXED_PRICE',         // Sabit Fiyat
  TIME_AND_MATERIAL = 'TIME_AND_MATERIAL', // Zaman & Materyal
  RETAINER = 'RETAINER',              // Aylık sabit ücret
}

export enum MilestoneStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  DELAYED = 'DELAYED',
}

export enum TaskStatus {
  BACKLOG = 'BACKLOG',
  IN_PROGRESS = 'IN_PROGRESS',
  IN_REVIEW = 'IN_REVIEW',
  DONE = 'DONE',
  CANCELLED = 'CANCELLED',
}

export enum TaskPriority {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  CRITICAL = 'CRITICAL',
}

/**
 * Timesheet durumu:
 * DRAFT → SUBMITTED → APPROVED | REJECTED
 * [IK-03] Onaylı + aylık kapanış yapılmış timesheet değiştirilemez.
 */
export enum TimesheetStatus {
  DRAFT = 'DRAFT',
  SUBMITTED = 'SUBMITTED',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
}

export enum DayType {
  WORK = 'WORK',
  ANNUAL_LEAVE = 'ANNUAL_LEAVE',       // Yıllık izin
  SICK_LEAVE = 'SICK_LEAVE',           // Hastalık izni
  PUBLIC_HOLIDAY = 'PUBLIC_HOLIDAY',   // Resmi tatil
  UNPAID_LEAVE = 'UNPAID_LEAVE',       // Ücretsiz izin
}

export enum Currency {
  TRY = 'TRY',
  USD = 'USD',
  EUR = 'EUR',
  GBP = 'GBP',
}

/**
 * Kesinti yöntemi
 */
export enum DeductionMethod {
  PERCENTAGE = 'PERCENTAGE',
  FIXED_AMOUNT = 'FIXED_AMOUNT',
}

/**
 * Kesinti matrahi
 */
export enum DeductionBase {
  GROSS = 'GROSS',
  SUBTOTAL = 'SUBTOTAL',
  NET_AFTER_KDV = 'NET_AFTER_KDV',
}

export enum NotificationType {
  BURN_RATE_WARNING = 'BURN_RATE_WARNING',       // [IK-05]
  TIMESHEET_REMINDER = 'TIMESHEET_REMINDER',
  TIMESHEET_APPROVED = 'TIMESHEET_APPROVED',
  TIMESHEET_REJECTED = 'TIMESHEET_REJECTED',
  PROJECT_ACTIVATED = 'PROJECT_ACTIVATED',       // [IK-09]
  PROPOSAL_WON = 'PROPOSAL_WON',
  MONTHLY_CLOSING = 'MONTHLY_CLOSING',
  CAPACITY_WARNING = 'CAPACITY_WARNING',         // [IK-01]
  DELAYED_ENTRY_ALERT = 'DELAYED_ENTRY_ALERT',
  PLAN_CHANGED = 'PLAN_CHANGED',                 // Plan değişikliği bildirimi
  PLAN_UPGRADE = 'PLAN_UPGRADE',                 // Plan yükseltme (Super Admin'e)
  PLAN_DOWNGRADE = 'PLAN_DOWNGRADE',             // Plan düşürme (Super Admin'e)
  PAYMENT_SUCCESS = 'PAYMENT_SUCCESS',           // Ödeme başarılı
  PAYMENT_FAILED = 'PAYMENT_FAILED',             // Ödeme başarısız
  LEAVE_REQUEST_SUBMITTED = 'LEAVE_REQUEST_SUBMITTED', // İzin talebi gönderildi (yöneticiye)
  LEAVE_REQUEST_APPROVED = 'LEAVE_REQUEST_APPROVED',   // İzin onaylandı (çalışana)
  LEAVE_REQUEST_REJECTED = 'LEAVE_REQUEST_REJECTED',   // İzin reddedildi (çalışana)
}

/**
 * İzin talebi durum makinesi:
 * DRAFT → SUBMITTED → APPROVED | REJECTED
 * DRAFT/SUBMITTED → CANCELLED (talep sahibi iptal edebilir)
 */
export enum LeaveRequestStatus {
  DRAFT = 'DRAFT',
  SUBMITTED = 'SUBMITTED',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  CANCELLED = 'CANCELLED',
}

export enum LeaveTypeCode {
  ANNUAL = 'ANNUAL',               // Yıllık izin
  SICK = 'SICK',                   // Hastalık izni
  UNPAID = 'UNPAID',               // Ücretsiz izin
  COMPASSIONATE = 'COMPASSIONATE', // İcazet (evlilik, ölüm vb.)
}
